import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { X } from "lucide-react";
import { RETENTION_DAYS } from "@/config/retention";
import { useUserStore } from "@/stores/user-store";

const DISMISSED_KEY = "animepup:retention-banner-dismissed";

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Signed-in notice that files are deleted after the retention window. Dismissal is
 * read synchronously on first render, so a dismissed banner never flashes in.
 */
export function RetentionBanner() {
  const { isSignedIn } = useAuth();
  const { isWhale } = useUserStore();
  const [dismissed, setDismissed] = useState(readDismissed);

  if (!isSignedIn || dismissed) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      // Private mode: hidden for this visit only.
    }
    setDismissed(true);
  };

  return (
    // Equal padding on both sides keeps the text centred and clear of the ×.
    <div className="relative shrink-0 z-40 border-b border-white/6 bg-[#1a1520] px-8 sm:px-10 py-1.5 text-center text-[11px] sm:text-[12px] leading-snug text-muted-foreground">
      <p className="text-balance">
        {/* VIPs get an unspecified longer window, so no number for them. */}
        {isWhale
          ? "As a VIP, you get extended access to your uploads and videos."
          : <>Your uploads and videos are deleted after {RETENTION_DAYS}{"\u00a0"}days.</>}{" "}
        <Link to="/assets" className="text-foreground underline underline-offset-2 hover:text-[#d4a574] transition-colors">
          Save your favourites from the library.
        </Link>
      </p>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-1.5 sm:right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
